import savedPanelsStore, { IPanelCounterparty, IPanelSelection } from "./savedPanelsStore";

import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { fetchRequestWrapper } from "@/lib/utilities";
import { SetStateAction } from "react";

export interface IUserPanels {
  id: number;
  displayName: string;
  favourite: boolean;
  counterparties: IPanelCounterparty[];
  active?: boolean;
}

export const defaultUserPanel: IPanelSelection = {
  id: "-1",
  label: "Panel",
  favourite: false,
  counterparties: [],
};

// - Generate a new config for the Database - the database will manage the id, need to create a unique Label here via the index number.
export function generateNewPanel(): IUserPanels {
  const currentPanel = savedPanelsStore.getState().userPanels;

  // Extract existing numbers from panel names
  const numbers = currentPanel
    .map((panel) => {
      const match = panel.label.match(/^New Panel(?: (\d+))?$/);
      return match ? (match[1] ? parseInt(match[1], 10) : 0) : null;
    })
    .filter((num): num is number => num !== null) // Type guard to ensure TypeScript knows num is a number
    .sort((a, b) => a - b); // Sort ascending

  // Find the first missing number
  let nextNumber = 0;
  for (let i = 0; i < numbers.length; i++) {
    if (numbers[i] !== i) {
      nextNumber = i;
      break;
    }
    nextNumber = i + 1;
  }

  return {
    id: 0,
    displayName: nextNumber === 0 ? `New Panel` : `New Panel ${nextNumber}`,
    favourite: false,
    counterparties: [],
  };
}

// First load of data from backend.
export async function initialPanelsFromAPI(
  // Can't set the counterparties list in this function as the valid counterparties list is needed.
  getAccessTokenSilently: Function,
  setCurrentActiveTab: React.Dispatch<SetStateAction<IPanelSelection>>,
  setIsDefault: React.Dispatch<SetStateAction<boolean>>,
  currentStoreTrade: any,
  isOrder?: boolean,
) {
  const currentZustandPanels = savedPanelsStore.getState().userPanels;
  let panelToSet: IPanelSelection[] = [];

  function updatedPanelActiveForOrder(
    panels: IPanelSelection[],
  ): IPanelSelection[] {
    if (!isOrder) {
      return panels;
    }

    const activePanel = panels.find((p) => p.active);
    // If Order mode and active panel has more than 1 counterparty, reset to default
    if (
      activePanel &&
      activePanel.counterparties &&
      activePanel.counterparties.length > 1
    ) {
      return panels.map((panel) => ({
        ...panel,
        active: panel.id === "-1", // -1 is the default.
      }));
    }

    return panels;
  }

  if (currentZustandPanels.length === 0) {
    const panelsResponse = await fetchRequestWrapper(
      { url: "/UserSettings/Panels", method: "GET" },
      getAccessTokenSilently,
    );

    const responseFromAPI = panelsResponse.panels;
    const convertedPanelsWithLabelMap: IPanelSelection[] =
      returnMappedItemFromDatabaseForZustandOnLoad(responseFromAPI);

    const findDefault = convertedPanelsWithLabelMap.find(
      (panel) => panel.id === "-1",
    );
    if (!findDefault) {
      convertedPanelsWithLabelMap.unshift(defaultUserPanel);
    }
    panelToSet = convertedPanelsWithLabelMap;
  } else {
    // use the existing zustand state.
    panelToSet = currentZustandPanels;
  }

  if (currentStoreTrade?.status === ExecutionStrategyStatus.New) {
    // check if there is a favourite option, if so set it to active, if not, set default active.
    const returnedFavouriteItem = panelToSet.find((panel) => panel.favourite);
    if (returnedFavouriteItem) {
      panelToSet = panelToSet.map((panel) =>
        panel.favourite ? { ...panel, active: true } : panel,
      );
      // Also set the favourite to be the active panel item
      // NOTE: The actual CP's selected on the grid will happen where the valid CP's are called.
      setCurrentActiveTab(returnedFavouriteItem);
      //Fav found means not default
      setIsDefault(false);
    } else {
      panelToSet = panelToSet.map((panel) =>
        panel.id === "-1" ? { ...panel, active: true } : panel,
      );
      setIsDefault(true);
    }
  } else {
    setIsDefault(true);
  }

  // Apply Order mode validation - reset to default if active panel has >1 CP
  panelToSet = updatedPanelActiveForOrder(panelToSet);

  // Check if Order mode caused a reset to default panel
  const activePanel = panelToSet.find((p) => p.active);
  if (isOrder && activePanel?.id === "-1") {
    setCurrentActiveTab(defaultUserPanel);
    setIsDefault(true);
  }

  savedPanelsStore.getState().setUserPanels(panelToSet);
}

// update the active panel.
export function setPanelItemAsActive(item: IPanelSelection) {
  // Does not need to be sent to the database.
  const newPanelsCopy = savedPanelsStore.getState().userPanels.map((cp) => {
    return { ...cp, active: cp.id.toString() === item.id ? true : false };
  });
  savedPanelsStore.getState().setUserPanels(newPanelsCopy);
  // UPDATE the CPs selected
}

// update the favourite.
export function setPanelItemAsfavourite(
  item: IPanelSelection,
  getAccessTokenSilently: Function,
) {
  const oldFav = savedPanelsStore
    .getState()
    .userPanels.find((panel) => panel.favourite);
  // Second step in to remove the favourite flag from the old fav
  if (oldFav) {
    const itemWithFavRemoved = { ...oldFav, favourite: false };
    updateAnExistingPanel(itemWithFavRemoved, getAccessTokenSilently); // update the DB
  }

  // Does not need to be sent to the database.
  const newPanelsCopy = savedPanelsStore.getState().userPanels.map((cp) => {
    return { ...cp, favourite: cp.id.toString() === item.id ? true : false };
  });
  savedPanelsStore.getState().setUserPanels(newPanelsCopy);

  const itemWithFav = { ...item, favourite: true };
  updateAnExistingPanel(itemWithFav, getAccessTokenSilently); // update the DB
}
// Delete a panel.
export async function deletPanelItem(
  item: IPanelSelection,
  handleSelectedPanelChange: Function,
  getAccessTokenSilently: Function,
) {
  const currentPanel = savedPanelsStore.getState().userPanels;
  if (currentPanel.length === 0) return;
  const ID = Number(item.id);
  await fetchRequestWrapper(
    {
      url: `/UserSettings/Panels/${ID}`,
      method: "DELETE",
    },
    getAccessTokenSilently,
  );

  const filteredPanel = currentPanel.filter(
    (setting) => Number(setting.id) !== Number(item.id),
  );

  if (filteredPanel.length === 0) return; // Prevent errors if all options are deleted
  // Above line should never happen as the default cannot be deleted.

  const hasfavourite = filteredPanel.some((setting) => setting.favourite);
  const hasActive = filteredPanel.some((setting) => setting.active);

  const updatedPanel = filteredPanel.map((setting) => ({
    ...setting,
    favourite: hasfavourite ? setting.favourite : setting.id === "-1",
    active: hasActive ? setting.active : setting.id === "-1",
  }));

  // updateStore
  savedPanelsStore.getState().setUserPanels(updatedPanel);

  const newActive = updatedPanel.find((setting) => setting.active);
  handleSelectedPanelChange(newActive); // Need to check that panels are getting updated.
}
// ----- Add a new panel ----- //
export async function createNewPanel(getAccessTokenSilently: Function) {
  const newPanel = generateNewPanel();
  const responseFromAPI = await fetchRequestWrapper(
    {
      url: `/UserSettings/Panels`,
      method: "POST",
      objToSend: newPanel,
    },
    getAccessTokenSilently,
  );
  const mappedAPIResponse =
    returnMappedItemFromDatabaseForZustandSingle(responseFromAPI);
  // The API call deletes the Panel name. Need to save back over it.
  mappedAPIResponse.label = newPanel.displayName;

  const currentPanel = savedPanelsStore.getState().userPanels;
  const updatedPanel = [...currentPanel, mappedAPIResponse];
  savedPanelsStore.getState().setUserPanels(updatedPanel);
}

// ----- Update the counterparties array of a panel. ----- //
export function updatePanelCounterparties(
  activeCounterPartyConfig: IPanelCounterparty[],
  getAccessTokenSilently: Function,
) {
  const currentPanel = savedPanelsStore
    .getState()
    .userPanels.find((panel) => panel.active);
  if (currentPanel) {
    currentPanel.counterparties = activeCounterPartyConfig;
    updateAnExistingPanel(currentPanel, getAccessTokenSilently); // updates the DB.
    const currentZustand = savedPanelsStore.getState().userPanels;
    const updatedZustand = currentZustand.map((panel) => {
      // updates the store.
      return {
        ...panel,
        counterparties:
          panel.id === currentPanel!.id
            ? activeCounterPartyConfig
            : panel.counterparties,
      };
    });
    savedPanelsStore.getState().setUserPanels(updatedZustand);
  }
}

export async function renamePanelLabel(
  newActiveTab: IPanelSelection,
  getAccessTokenSilently: Function,
) {
  // update Zustand
  const currentZustand = savedPanelsStore.getState().userPanels;
  const updatedZustand = currentZustand.map((panel) => {
    return {
      ...panel,
      label: panel.id === newActiveTab.id ? newActiveTab.label : panel.label,
    };
  });

  savedPanelsStore.getState().setUserPanels(updatedZustand);

  // update the DB
  updateAnExistingPanel(newActiveTab, getAccessTokenSilently);
}

// -- called in all functions above that need DB update -- //
// ----- Generic call to update a panel - will be used for fav and renaming. ----- //
export async function updateAnExistingPanel(
  item: IPanelSelection,
  getAccessTokenSilently: Function,
) {
  // update BOTH the id AND the label to a display name.
  const convertedIdItem = returnedMappedIdAndDisplayName(item);
  // call DB here

  // let responseFromAPI = false
  console.log(convertedIdItem)
  const responseFromAPI = await fetchRequestWrapper(
    {
      url: `/UserSettings/Panels`,
      method: "PUT",
      objToSend: convertedIdItem,
    },
    getAccessTokenSilently,
  );
  if (responseFromAPI) {
    return true;
  } else {
    return false;
  }
}

// ----- CONVERT STORE ITEM TO DB ITEM: Mapping of ID and Display Name ----- //
// // -- Zustand to OUTGOING message -- // //
function returnedMappedIdAndDisplayName(item: IPanelSelection): IUserPanels {
  const itemToReturn = {
    id: Number(item.id),
    displayName: item.label,
    favourite: item.favourite ? true : false,
    counterparties: item.counterparties ? item.counterparties : [],
  };

  return itemToReturn;
}

// ----- CONVERT DataBase Response TO DB ITEM: Mapping of ID and Display Name ----- //
// // -- DataBase to Zustand -- Can be used on first load OR without an index // //
function returnMappedItemFromDatabaseForZustandOnLoad(
  responseFromAPI: IUserPanels[],
) {
  const convertedPanelsWithLabelMap: IPanelSelection[] = responseFromAPI.map(
    (panel: IUserPanels) => ({
      id: panel.id.toString(),
      label: panel.displayName,
      counterparties: panel.counterparties,
      active: panel.active,
      favourite: panel.favourite,
    }),
  );

  return convertedPanelsWithLabelMap;
}
export function returnMappedItemFromDatabaseForZustandSingle(
  responseFromAPI: IUserPanels,
): IPanelSelection {
  const stringId = responseFromAPI.id.toString() || "";
  // If there is no index just map the displayName to panel.
  const convertedPanelsWithLabelMap: IPanelSelection = {
    id: stringId,
    label: responseFromAPI.displayName,
    counterparties: responseFromAPI.counterparties,
    active: responseFromAPI.active,
    favourite: responseFromAPI.favourite,
  };

  return convertedPanelsWithLabelMap;
}
